<template>
  <div class="cc-root">
    <!-- LOADING SCREEN -->
    <Transition name="loading-fade">
      <div v-if="showLoading" class="cc-loading">
        <div class="cc-loader">
          <div
            v-for="n in 5"
            :key="n"
            class="loader-candle"
            :style="{ animationDelay: `${n * 0.13}s` }"
          ></div>
        </div>
        <h1 class="cc-loading-title">
          Candle<span class="cc-accent">Clash</span>
        </h1>
        <div class="cc-loading-bar">
          <div
            class="cc-loading-fill"
            :style="{ width: `${loadingProgress}%` }"
          ></div>
        </div>
        <p class="cc-loading-label cc-mono">{{ loadingLabel }}</p>
      </div>
    </Transition>

    <!-- HEADER -->
    <header class="cc-header">
      <div class="cc-brand">
        <div class="cc-brand-badge">
          <svg viewBox="0 0 24 24" class="cc-badge-svg" fill="none">
            <rect x="4" y="10" width="3" height="8" rx="1" fill="#00e676" />
            <line
              x1="5.5"
              y1="7"
              x2="5.5"
              y2="20"
              stroke="#00e676"
              stroke-width="1.2"
            />
            <rect x="10.5" y="6" width="3" height="7" rx="1" fill="#ff1744" />
            <line
              x1="12"
              y1="4"
              x2="12"
              y2="15"
              stroke="#ff1744"
              stroke-width="1.2"
            />
            <rect x="17" y="9" width="3" height="6" rx="1" fill="#00e5ff" />
            <line
              x1="18.5"
              y1="6"
              x2="18.5"
              y2="17"
              stroke="#00e5ff"
              stroke-width="1.2"
            />
          </svg>
        </div>
        <div class="cc-brand-meta">
          <h1 class="cc-brand-name">
            Candle<span class="cc-accent">Clash</span>
          </h1>
          <span class="cc-brand-dot">/</span>
          <span class="cc-brand-sub">Volatility</span>
          <span :class="['cc-status-chip', isLive ? 'is-live' : 'is-local']">
            <span class="cc-status-dot"></span>
            {{ isLive ? "LIVE" : "LOCAL" }}
          </span>
        </div>
      </div>
      <div class="cc-header-right">
        <div class="cc-balance">
          <p class="cc-balance-label">Balance</p>
          <p class="cc-balance-value cc-mono">{{ balanceDisplay }}</p>
        </div>
        <button
          @click="toggleMute"
          :aria-label="muted ? 'Unmute sound' : 'Mute sound'"
          :title="muted ? 'Unmute sound' : 'Mute sound'"
          class="cc-icon-btn"
        >
          <svg
            viewBox="0 0 24 24"
            class="cc-icon"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 9v6h4l5 4V5L7 9H3z" fill="currentColor" />
            <template v-if="muted">
              <line x1="16" y1="9" x2="22" y2="15" />
              <line x1="22" y1="9" x2="16" y2="15" />
            </template>
            <template v-else>
              <path d="M15.5 8.5a5 5 0 0 1 0 7" />
              <path d="M18.5 6a9 9 0 0 1 0 12" />
            </template>
          </svg>
        </button>
      </div>
    </header>

    <!-- MAIN GAME AREA -->
    <main class="cc-main">
      <!-- LEFT: GAME BOARD -->
      <div class="cc-board">
        <!-- STAGE / CHART PANE -->
        <div
          :class="[
            'cc-stage',
            stageShake && 'stage-shake',
            isFinalTick && 'stage-final',
          ]"
        >
          <!-- Impact flash (moon / crash) -->
          <div
            v-if="flashColor"
            class="cc-flash stage-flash"
            :style="{ background: flashColor }"
          ></div>

          <!-- Win confetti -->
          <div class="cc-confetti-layer">
            <div
              v-for="p in particles"
              :key="p.id"
              class="confetti"
              :style="{
                left: `${p.x}%`,
                top: `${p.y}%`,
                background: p.color,
                '--dx': `${p.dx}px`,
                '--dy': `${p.dy}px`,
                animationDelay: `${p.delay}s`,
              }"
            ></div>
          </div>

          <!-- Chart type toggle -->
          <div class="cc-chart-toggle">
            <button
              type="button"
              :class="[
                'cc-chart-toggle-btn',
                chartType === 'candles' && 'is-active',
              ]"
              @click="setChartType('candles')"
              title="Candlestick view"
              aria-label="Candlestick view"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <line x1="7" y1="3" x2="7" y2="21" />
                <rect
                  x="4.5"
                  y="7"
                  width="5"
                  height="8"
                  rx="1"
                  fill="currentColor"
                  stroke="none"
                />
                <line x1="17" y1="3" x2="17" y2="21" />
                <rect
                  x="14.5"
                  y="9"
                  width="5"
                  height="7"
                  rx="1"
                  fill="currentColor"
                  stroke="none"
                />
              </svg>
            </button>
            <button
              type="button"
              :class="[
                'cc-chart-toggle-btn',
                chartType === 'line' && 'is-active',
              ]"
              @click="setChartType('line')"
              title="Line view"
              aria-label="Line view"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="3,15 9,9 13,13 21,5" />
              </svg>
            </button>
          </div>

          <!-- BULL SIDE (Left) -->
          <div class="cc-side">
            <div class="cc-side-label is-bull">
              <span class="cc-mono">&#9650;</span> BULLS
            </div>
            <div class="card-slot cc-card">
              <div
                :class="[
                  'cc-card-face cc-mono',
                  bullBounce && 'card-flip',
                  bullCard === '?' ? 'is-empty' : 'is-bull',
                ]"
              >
                {{ bullCard }}
              </div>
            </div>
          </div>

          <!-- CENTER: CANDLESTICK CHART -->
          <div class="cc-chart">
            <svg
              viewBox="0 0 150 240"
              preserveAspectRatio="xMidYMid meet"
              class="cc-chart-svg"
            >
              <text
                x="75"
                y="10"
                text-anchor="middle"
                class="chart-label"
                fill="#b14bff"
              >
                &#9650; MOON +5
              </text>
              <text
                x="75"
                y="236"
                text-anchor="middle"
                class="chart-label"
                fill="#ff6d00"
              >
                &#9660; CRASH &#8722;5
              </text>

              <line
                v-for="v in gridValues"
                :key="`grid-${v}`"
                x1="8"
                :y1="yFor(v)"
                x2="142"
                :y2="yFor(v)"
                stroke="#3a2a52"
                stroke-width="1"
                opacity="0.7"
              />
              <line
                x1="8"
                :y1="yFor(0)"
                x2="142"
                :y2="yFor(0)"
                stroke="#a596c4"
                stroke-width="1"
                stroke-dasharray="3 3"
                opacity="0.55"
              />
              <line
                x1="8"
                :y1="yFor(5)"
                x2="142"
                :y2="yFor(5)"
                stroke="#b14bff"
                stroke-width="1.5"
                opacity="0.7"
              />
              <line
                x1="8"
                :y1="yFor(-5)"
                x2="142"
                :y2="yFor(-5)"
                stroke="#ff6d00"
                stroke-width="1.5"
                opacity="0.7"
              />

              <!-- Candlestick view -->
              <template v-if="chartType === 'candles'">
                <g
                  v-for="(c, i) in candles"
                  :key="`candle-${i}`"
                  class="candle"
                >
                  <line
                    :x1="candleX(i)"
                    :y1="yFor(c.hi)"
                    :x2="candleX(i)"
                    :y2="yFor(c.lo)"
                    :stroke="candleColor(c)"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                  <rect
                    :x="candleX(i) - 7"
                    :y="bodyY(c)"
                    width="14"
                    :height="bodyHeight(c)"
                    rx="2"
                    :fill="candleColor(c)"
                  />
                </g>
              </template>

              <!-- Line view -->
              <template v-else>
                <polyline
                  v-if="linePoints"
                  :points="linePoints"
                  fill="none"
                  :stroke="priceColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="cc-line"
                />
                <circle
                  v-for="(c, i) in candles"
                  :key="`pt-${i}`"
                  :cx="candleX(i)"
                  :cy="yFor(c.close)"
                  r="2.4"
                  :fill="candleColor(c)"
                />
              </template>

              <line
                x1="8"
                :y1="yFor(currentValue)"
                x2="142"
                :y2="yFor(currentValue)"
                :stroke="priceColor"
                stroke-width="1"
                stroke-dasharray="4 3"
                opacity="0.9"
              />
              <circle
                cx="136"
                :cy="yFor(currentValue)"
                r="3.5"
                :fill="priceColor"
                class="price-dot"
              />

              <text
                v-if="candles.length === 0 && !isPlaying"
                x="75"
                y="112"
                text-anchor="middle"
                class="chart-empty"
                fill="#6b5b8a"
              >
                AWAITING OPEN
              </text>
            </svg>
          </div>

          <!-- BEAR SIDE (Right) -->
          <div class="cc-side">
            <div class="cc-side-label is-bear">
              <span class="cc-mono">&#9660;</span> BEARS
            </div>
            <div class="card-slot cc-card">
              <div
                :class="[
                  'cc-card-face cc-mono',
                  bearBounce && 'card-flip',
                  bearCard === '?' ? 'is-empty' : 'is-bear',
                ]"
              >
                {{ bearCard }}
              </div>
            </div>
          </div>

          <!-- Status Overlay -->
          <div class="cc-status-overlay">
            <div
              :class="[
                'cc-status-text',
                statusColor,
                statusVisible ? 'is-visible' : 'is-hidden',
              ]"
            >
              {{ statusText }}
            </div>
          </div>

          <!-- Per-tick Decision Modal -->
          <div v-if="showDecision" class="cc-cashout">
            <div class="cc-cashout-title">
              Tick {{ tickCount }} / {{ maxTick }} — Your Call
            </div>
            <div class="cc-cashout-actions">
              <button
                @click="chooseProfit"
                :disabled="profitAmount <= 0"
                class="cc-btn cc-btn-profit"
              >
                TAKE PROFIT
                <span class="cc-mono">{{
                  profitAmount > 0 ? `+$${profitAmount}` : "—"
                }}</span>
              </button>
              <button @click="chooseHold" class="cc-btn cc-btn-hold">
                HOLD
              </button>
              <button
                @click="chooseLoss"
                :disabled="lossRefund <= 0"
                class="cc-btn cc-btn-loss"
              >
                TAKE LOSS
                <span class="cc-mono">{{
                  lossRefund > 0 ? `+$${lossRefund}` : "—"
                }}</span>
              </button>
            </div>
            <p class="cc-cashout-note">
              Lock the guaranteed value, cut losses for a partial refund, or
              hold for the next tick.
            </p>
          </div>
        </div>

        <!-- HISTORY LOG -->
        <div class="cc-history">
          <span class="cc-history-label">History</span>
          <div
            v-for="(val, i) in history"
            :key="i"
            :class="historyPillClass(val)"
          >
            {{ historyPillText(val) }}
          </div>
        </div>
      </div>

      <!-- RIGHT: ORDER PANEL -->
      <div class="cc-panel">
        <!-- Bet Amount Selection -->
        <div class="cc-field">
          <label class="cc-field-label">{{ words.wagerAmount }}</label>
          <div class="cc-chips">
            <button
              v-for="amount in wagerOptions"
              :key="amount"
              @click="setWager(amount)"
              :class="['cc-chip cc-mono', wager === amount && 'is-selected']"
            >
              {{ formatWager(amount) }}
            </button>
          </div>
          <input
            v-model="customWagerInput"
            type="number"
            min="0"
            inputmode="decimal"
            placeholder="Custom amount"
            :disabled="isPlaying"
            @keyup.enter="applyCustomWager"
            @blur="applyCustomWager"
            :class="['cc-input cc-mono', isCustomWager && 'is-active']"
          />
        </div>

        <div class="cc-divider"></div>

        <!-- Main Bets: Long / Short -->
        <div class="cc-bets-2">
          <button
            @click="placeBet('bull')"
            :class="['cc-bet is-bull', betBtnClass('bull')]"
          >
            <div class="cc-bet-mult cc-mono">x1.4&#8211;x6</div>
            <span class="cc-bet-arrow">&#9650;</span>
            <span class="cc-bet-name">BULL</span>
            <span class="cc-bet-desc">Long the market</span>
          </button>

          <button
            @click="placeBet('bear')"
            :class="['cc-bet is-bear', betBtnClass('bear')]"
          >
            <div class="cc-bet-mult cc-mono">x1.4&#8211;x6</div>
            <span class="cc-bet-arrow">&#9660;</span>
            <span class="cc-bet-name">BEAR</span>
            <span class="cc-bet-desc">Short the market</span>
          </button>
        </div>

        <!-- Side Bet: Doji -->
        <button
          @click="placeBet('doji')"
          :class="['cc-bet-row', betBtnClass('doji')]"
        >
          <div class="cc-bet-row-left">
            <span class="cc-bet-row-icon">&#8212;</span>
            <div class="cc-bet-row-meta">
              <span class="cc-bet-row-name">THE DOJI</span>
              <span class="cc-bet-row-desc"
                >Exactly 0 &bull; near miss (&plusmn;1) pays x2</span
              >
            </div>
          </div>
          <span class="cc-bet-row-pay cc-mono">x9.0</span>
        </button>

        <div class="cc-bets-2">
          <button
            @click="placeBet('moon')"
            :class="['cc-bet-mini is-moon', betBtnClass('moon')]"
          >
            <div class="cc-bet-mini-sub">HITS TOP (+5)</div>
            <div class="cc-bet-mini-name">
              MOON <span class="cc-mono">x21</span>
            </div>
          </button>
          <button
            @click="placeBet('crash')"
            :class="['cc-bet-mini is-crash', betBtnClass('crash')]"
          >
            <div class="cc-bet-mini-sub">HITS BOT (-5)</div>
            <div class="cc-bet-mini-name">
              CRASH <span class="cc-mono">x21</span>
            </div>
          </button>
        </div>

        <!-- Action Button -->
        <button
          @click="startGame"
          :disabled="!actionEnabled"
          :class="['cc-action', actionEnabled ? 'is-ready' : 'is-disabled']"
        >
          {{ actionLabel }}
        </button>

        <div
          :class="[
            'cc-betinfo',
            errorMessage || (isLive && rgs.initError.value) ? 'is-error' : '',
          ]"
        >
          {{ betDisplay }}
        </div>
      </div>
    </main>

    <!-- FOOTER -->
    <footer class="cc-footer">
      <p>
        PAYS: Bull/Bear hold ladder x1.4&ndash;x6 &bull; Doji x9 (near miss x2)
        &bull; Moon/Crash x21
      </p>
      <p class="cc-footer-sub">
        Every tick you choose: TAKE PROFIT (lock the mark), TAKE LOSS (partial
        refund), or HOLD &mdash; up to {{ maxTick }} ticks
      </p>
      <p class="cc-legal">
        Malfunction voids all wins and plays. A consistent internet connection
        is required. In the event of a disconnection, reload the game to finish
        any uncompleted rounds. The expected return is calculated over many
        plays. The game display is not representative of any physical device and
        is for illustrative purposes only. Winnings are settled according to the
        amount received from the Remote Game Server and not from events within
        the web browser. TM and &copy; 2026 Stake Engine.
      </p>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { DisplayAmount, ParseAmount } from "stake-engine";
import type { Round } from "stake-engine";
import { useRgs } from "../composables/useRgs";
import { useSound } from "../composables/useSound";

type BetType = "bull" | "bear" | "doji" | "moon" | "crash";

// Play modes sent to the RGS. These must match the modes defined in the
// math model deployed for this game on Stake Engine.
const BET_MODE: Record<BetType, string> = {
  bull: "bull",
  bear: "bear",
  doji: "doji",
  moon: "moon",
  crash: "crash",
};

// RGS amounts are fixed-point integers: 1.00 == 1_000_000
const API_MULTIPLIER = 1_000_000;

// v3 payouts — must match games/candle_clash in the math SDK.
// Hold-to-cap distance ladder: the further the price finishes in your
// direction, the bigger the win.
const DISTANCE_LADDER: Record<number, number> = {
  1: 1.4,
  2: 1.8,
  3: 2.5,
  4: 3.5,
  5: 6.0,
};
// Take-profit ladder: lock in the current mark-to-market by favourable distance.
const TAKE_PROFIT_LADDER: Record<number, number> = {
  1: 1.3,
  2: 1.8,
  3: 2.6,
  4: 3.8,
};
// Take-loss ladder: cut losses for a partial refund that decays the further
// the position is underwater (adverse distance).
const TAKE_LOSS_REFUND: Record<number, number> = {
  1: 0.7,
  2: 0.5,
  3: 0.3,
  4: 0.1,
};
const PAYOUTS = {
  moon: 21,
  crash: 21,
  dojiExact: 9,
  dojiNear: 2,
};

// --- STAKE ENGINE (RGS) ---
const rgs = useRgs();
const isLive = rgs.isRgsMode;

// --- SOCIAL MODE WORDING ---
// Stake Engine requirement: when launched with social=true, restricted
// gambling phrases must be replaced (bet -> play, wager -> play amount,
// cash -> coins, ...). Real-money mode keeps the standard wording.
const isSocial = rgs.isSocialMode;
const words = isSocial
  ? {
      wagerAmount: "Play Amount",
      selectBet: "SELECT A PLAY",
      betVerb: "PLAY",
      betRejected: "PLAY REJECTED",
      cashedOut: "COLLECTED",
      insufficientFunds: "INSUFFICIENT COINS",
      strongBuy: "SURGE",
    }
  : {
      wagerAmount: "Wager Amount",
      selectBet: "SELECT A BET",
      betVerb: "BET",
      betRejected: "BET REJECTED",
      cashedOut: "CASHED OUT",
      insufficientFunds: "INSUFFICIENT FUNDS",
      strongBuy: "STRONG BUY",
    };

// --- SOUND (Web Audio synth, see useSound.ts) ---
const sound = useSound();
const { muted } = sound;

function toggleMute() {
  sound.toggleMute();
  sound.click();
}

// --- LOADING SCREEN ---
const showLoading = ref(true);
const loadingProgress = ref(0);
const loadingLabel = ref("BOOTING TERMINAL");
let loadingTimer: ReturnType<typeof setInterval> | null = null;

const LOADING_LABELS = [
  "BOOTING TERMINAL",
  "FETCHING MARKET DATA",
  "CALIBRATING VOLATILITY",
  "OPENING EXCHANGE",
];

function startLoading() {
  const startedAt = Date.now();
  let done = false;
  loadingTimer = setInterval(() => {
    const connecting = isLive && !rgs.ready.value && !rgs.initError.value;
    // Hold at 85% while waiting on the RGS handshake.
    const target = connecting ? 85 : 100;
    loadingProgress.value = Math.min(
      target,
      loadingProgress.value + 3 + Math.random() * 7
    );

    if (isLive && rgs.initError.value) {
      loadingLabel.value = "CONNECTION FAILED";
    } else {
      const idx = Math.min(
        LOADING_LABELS.length - 1,
        Math.floor(loadingProgress.value / 30)
      );
      loadingLabel.value = LOADING_LABELS[idx];
    }

    const minShown = Date.now() - startedAt >= 1400;
    const ready = !isLive || rgs.ready.value || !!rgs.initError.value;
    if (loadingProgress.value >= 100 && minShown && ready && !done) {
      done = true;
      if (loadingTimer) clearInterval(loadingTimer);
      loadingTimer = null;
      // Matches appReady timeline: progress completes, then overlay fades.
      setTimeout(() => (showLoading.value = false), 350);
    }
  }, 120);
}

onMounted(() => {
  if (isLive) rgs.init();
  startLoading();
  window.addEventListener("keydown", onSpacebar);
});
onUnmounted(() => {
  rgs.dispose();
  if (loadingTimer) clearInterval(loadingTimer);
  window.removeEventListener("keydown", onSpacebar);
});

// Approval requirement: spacebar maps to the bet button, unless the
// jurisdiction disables it.
function onSpacebar(e: KeyboardEvent) {
  if (e.code !== "Space" || e.repeat) return;
  if (rgs.jurisdictionFlags.value?.disabledSpacebar) return;
  if (showDecision.value) return;
  e.preventDefault();
  const active = document.activeElement;
  if (active instanceof HTMLElement) active.blur();
  startGame();
}

// --- GAME STATE ---
const demoBalance = ref(1000);
const wager = ref(100);
const currentBetType = ref<BetType | null>(null);
const isPlaying = ref(false);

const priceIndex = ref(5); // 0 to 10 (5 is Center/0)
const tickCount = ref(0);
const maxTick = 5; // per-tick decision cap
const volatility = ref(1);
const moonHit = ref(false);
const crashHit = ref(false);

// --- UI STATE ---
const errorMessage = ref("");

// --- STAGE EFFECTS (shake / flash / confetti / final-tick tension) ---
const stageShake = ref(false);
const flashColor = ref<string | null>(null);

interface Particle {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  color: string;
  delay: number;
}
const particles = ref<Particle[]>([]);
let particleId = 0;

const isFinalTick = computed(
  () => isPlaying.value && tickCount.value >= maxTick
);

const FLASH = {
  moon: "rgba(177, 75, 255, 0.5)",
  crash: "rgba(255, 109, 0, 0.5)",
};
const WIN_COLORS = ["#ffd23f", "#00e676", "#ff2e97", "#00e5ff"];

let shakeTimer: ReturnType<typeof setTimeout> | null = null;
function shakeStage() {
  stageShake.value = false;
  // Force a reflow-free retrigger on the next frame.
  requestAnimationFrame(() => (stageShake.value = true));
  if (shakeTimer) clearTimeout(shakeTimer);
  shakeTimer = setTimeout(() => (stageShake.value = false), 450);
}

let flashTimer: ReturnType<typeof setTimeout> | null = null;
function flashStage(color: string) {
  flashColor.value = null;
  requestAnimationFrame(() => (flashColor.value = color));
  if (flashTimer) clearTimeout(flashTimer);
  flashTimer = setTimeout(() => (flashColor.value = null), 650);
}

function burstConfetti(colors: string[] = WIN_COLORS, count = 28) {
  const burst: Particle[] = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const dist = 60 + Math.random() * 160;
    burst.push({
      id: particleId++,
      x: 46 + Math.random() * 8,
      y: 40 + Math.random() * 10,
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist - 80,
      color: colors[i % colors.length],
      delay: Math.random() * 0.15,
    });
  }
  particles.value = burst;
  setTimeout(() => {
    if (particles.value === burst) particles.value = [];
  }, 1300);
}

// --- CANDLESTICK CHART ---
// Values are in "price" space: +5 (moon, top) .. -5 (crash, bottom).
// priceIndex 0..10 maps to value = 5 - priceIndex.
interface Candle {
  open: number;
  close: number;
  hi: number;
  lo: number;
}

const candles = ref<Candle[]>([]);
// Chart rendering mode: candlesticks (default) or a close-price line.
const chartType = ref<"candles" | "line">("candles");
function setChartType(type: "candles" | "line") {
  if (chartType.value === type) return;
  chartType.value = type;
  sound.click();
}
const gridValues = [-4, -3, -2, -1, 1, 2, 3, 4];

const currentValue = computed(() => 5 - priceIndex.value);

const priceColor = computed(() => {
  const v = currentValue.value;
  if (v >= 5) return "#b14bff";
  if (v <= -5) return "#ff6d00";
  if (v > 0) return "#00e676";
  if (v < 0) return "#ff1744";
  return "#f5f0ff";
});

// viewBox is 150x240; value +5 -> y 20, value -5 -> y 220.
function yFor(v: number): number {
  return 120 - v * 20;
}

function candleX(i: number): number {
  const n = Math.max(candles.value.length, 3);
  const slot = 134 / n;
  return 8 + slot * i + slot / 2;
}

function candleColor(c: Candle): string {
  if (c.close > c.open) return "#00e676";
  if (c.close < c.open) return "#ff1744";
  return "#f5f0ff";
}

// Close-price polyline for the line chart: anchor at centre, then each close.
const linePoints = computed<string>(() => {
  if (candles.value.length === 0) return "";
  const pts = [`8,${yFor(0)}`];
  candles.value.forEach((c, i) => {
    pts.push(`${candleX(i)},${yFor(c.close)}`);
  });
  return pts.join(" ");
});

function bodyY(c: Candle): number {
  return yFor(Math.max(c.open, c.close));
}

function bodyHeight(c: Candle): number {
  return Math.max(4, Math.abs(c.open - c.close) * 20);
}

// Draw a candle for a tick and play the matching directional sound.
function pushCandle(prevIndex: number, newIndex: number) {
  const open = 5 - prevIndex;
  const close = 5 - newIndex;
  const up = close > open;
  const down = close < open;
  const wick = 0.15 + Math.random() * 0.35;
  const hi = Math.min(5, Math.max(open, close) + (up ? wick : wick * 0.5));
  const lo = Math.max(-5, Math.min(open, close) - (down ? wick : wick * 0.5));
  candles.value.push({ open, close, hi, lo });
  if (up) sound.tickUp();
  else if (down) sound.tickDown();
  else sound.tickFlat();
}

// Quick-pick chips. In live mode the operator dictates the valid bet
// levels, so each chip (and any custom amount) snaps to the nearest one.
const PREFERRED_WAGERS = [10, 50, 100, 500];

const allowedWagers = computed<number[]>(() => {
  if (isLive && rgs.config.value) {
    return rgs.config.value.betLevels.map((level) => ParseAmount(level));
  }
  return [];
});

const wagerOptions = computed<number[]>(() => {
  if (allowedWagers.value.length === 0) return PREFERRED_WAGERS;
  const snapped = PREFERRED_WAGERS.map((amount) => snapToBetLevel(amount));
  return [...new Set(snapped)];
});

function snapToBetLevel(amount: number): number {
  const levels = allowedWagers.value;
  if (levels.length === 0) return amount;
  return levels.reduce((best, level) =>
    Math.abs(level - amount) < Math.abs(best - amount) ? level : best
  );
}

const customWagerInput = ref("");

const isCustomWager = computed(() => !wagerOptions.value.includes(wager.value));

function applyCustomWager() {
  if (isPlaying.value) return;
  const value = Number.parseFloat(customWagerInput.value);
  if (!Number.isFinite(value) || value <= 0) {
    customWagerInput.value = "";
    return;
  }
  const snapped = snapToBetLevel(value);
  wager.value = snapped;
  if (snapped !== value) customWagerInput.value = String(snapped);
}

// Pick the operator's default bet level once authenticated.
watch(rgs.config, (config) => {
  if (config) wager.value = ParseAmount(config.defaultBetLevel);
});

const balanceDisplay = computed(() => {
  if (!isLive) return `$${demoBalance.value}`;
  return rgs.balance.value ? DisplayAmount(rgs.balance.value) : "—";
});

const canAfford = computed(() => {
  if (isLive) {
    return (rgs.balance.value?.amount ?? 0) >= wager.value * API_MULTIPLIER;
  }
  return demoBalance.value >= wager.value;
});
const bullCard = ref<string>("?");
const bearCard = ref<string>("?");
const bullBounce = ref(false);
const bearBounce = ref(false);
const statusText = ref("");
const statusColor = ref("cc-c-default");
const statusVisible = ref(false);
const history = ref<number[]>([]);
// Per-tick decision prompt state (Take Profit / Take Loss / Hold).
const showDecision = ref(false);
const profitAmount = ref(0); // guaranteed win if take profit (0 = not offered)
const lossRefund = ref(0); // partial refund if take loss (0 = not offered)
const hasPlayed = ref(false);

let statusTimer: ReturnType<typeof setTimeout> | null = null;

// --- BET BUTTON VISUALS ---
function betBtnClass(type: BetType): string {
  if (!currentBetType.value) return "";
  if (currentBetType.value === type) return "cc-bet-selected";
  return "cc-bet-dimmed";
}

// --- COMPUTED ACTION BUTTON ---
const actionEnabled = computed(() => {
  if (isPlaying.value || !currentBetType.value) return false;
  if (isLive && !rgs.ready.value) return false;
  return canAfford.value;
});

const actionLabel = computed(() => {
  if (isLive && rgs.initError.value) return "CONNECTION FAILED";
  if (isLive && !rgs.ready.value) return "CONNECTING...";
  if (isPlaying.value) return "MARKET OPEN";
  if (hasPlayed.value && !currentBetType.value) return "PLAY AGAIN";
  if (!currentBetType.value) return words.selectBet;
  if (!canAfford.value) return words.insufficientFunds;
  return `${words.betVerb} ${formatWager(
    wager.value
  )} ON ${currentBetType.value.toUpperCase()}`;
});

const betDisplay = computed(() => {
  if (isLive && rgs.initError.value) return rgs.initError.value;
  if (errorMessage.value) return errorMessage.value;
  if (isPlaying.value || !currentBetType.value || !canAfford.value) return "";
  return `Potential Win: up to ${formatWager(calculatePotentialWin())}`;
});

function formatWager(amount: number): string {
  const currency = rgs.balance.value?.currency;
  if (isLive && currency) {
    return DisplayAmount(
      { amount: Math.round(amount * API_MULTIPLIER), currency },
      { trimDecimalForIntegers: true }
    );
  }
  return `$${amount}`;
}

function calculatePotentialWin(): number {
  if (currentBetType.value === "doji") return wager.value * PAYOUTS.dojiExact;
  if (currentBetType.value === "moon" || currentBetType.value === "crash")
    return wager.value * PAYOUTS.moon;
  return wager.value * DISTANCE_LADDER[5];
}

// --- BETTING LOGIC ---
function setWager(amount: number) {
  if (isPlaying.value) return;
  wager.value = amount;
  customWagerInput.value = "";
  sound.chip();
}

function placeBet(type: BetType) {
  if (isPlaying.value) return;
  currentBetType.value = type;
  sound.click();
}

// --- GAME LOOP ---
async function startGame() {
  if (isPlaying.value || !actionEnabled.value) return;
  errorMessage.value = "";

  if (isLive) {
    await startServerRound();
    return;
  }

  isPlaying.value = true;
  demoBalance.value -= wager.value;
  resetBoard();
  sound.marketOpen();
  showStatus("MARKET OPEN", "cc-c-info");

  await wait(800);
  gameLoop();
}

function resetBoard() {
  candles.value = [];
  priceIndex.value = 5;
  tickCount.value = 0;
  volatility.value = 1;
  moonHit.value = false;
  crashHit.value = false;
  bullCard.value = "?";
  bearCard.value = "?";
}

// --- LIVE (STAKE ENGINE) ROUND ---
// The RGS decides the outcome when the bet is placed. With the candle_clash
// math model deployed, round.state carries the book's events for this round
// (roundStart / tick / cashOutOffer / setTotalWin / finalWin) and we replay
// them 1:1. If a server ever returns no events (e.g. a bare mock), we fall
// back to the old synthetic animation that lands on the win/lose verdict.

// Book event shapes — must match the math model (games/candle_clash).
// All book amounts are x100 bet multipliers (130 = 1.3x bet).
type BookTickLabel =
  | "bullish"
  | "strongBuy"
  | "bearish"
  | "panicSell"
  | "volatilitySpike"
  | "moonHit"
  | "crashHit";

type DecisionAction = "hold" | "takeProfit" | "takeLoss";

type BookEvent =
  | { index: number; type: "roundStart" }
  | {
      index: number;
      type: "tick";
      tick: number;
      bullCard: number;
      bearCard: number;
      movement: number;
      priceIndex: number;
      label: BookTickLabel;
    }
  | {
      index: number;
      type: "decision";
      tick: number;
      action: DecisionAction;
      amount: number;
    }
  | { index: number; type: "setTotalWin"; amount: number }
  | { index: number; type: "finalWin"; amount: number };

const TICK_LABEL_COLOR: Record<BookTickLabel, string> = {
  bullish: "cc-c-up",
  strongBuy: "cc-c-up",
  bearish: "cc-c-down",
  panicSell: "cc-c-down",
  volatilitySpike: "cc-c-moon",
  moonHit: "cc-c-moon",
  crashHit: "cc-c-crash",
};

function tickLabelText(event: Extract<BookEvent, { type: "tick" }>): string {
  switch (event.label) {
    case "strongBuy":
      return `${words.strongBuy} (+${Math.abs(event.movement)})`;
    case "panicSell":
      return `PANIC SELL (-${Math.abs(event.movement)})`;
    case "volatilitySpike":
      return "VOLATILITY SPIKE";
    case "moonHit":
      return "MOON HIT +5";
    case "crashHit":
      return "CRASH HIT −5";
    case "bullish":
      return "BULLISH";
    case "bearish":
      return "BEARISH";
  }
}

// Favourable distance for the current directional play: positive = in profit,
// negative = underwater (adverse distance).
function favorableDistance(): number {
  const v = 5 - priceIndex.value; // +5 (moon) .. -5 (crash)
  if (currentBetType.value === "bull") return v;
  if (currentBetType.value === "bear") return -v;
  return 0;
}

// Populate the Take Profit / Take Loss offer from the current board position.
function computeDecisionOffer() {
  const dist = favorableDistance();
  profitAmount.value =
    dist >= 1
      ? Math.floor(wager.value * TAKE_PROFIT_LADDER[Math.min(dist, 4)])
      : 0;
  lossRefund.value =
    dist <= -1
      ? Math.floor(wager.value * TAKE_LOSS_REFUND[Math.min(-dist, 4)])
      : 0;
}

async function replayBookEvents(events: BookEvent[]): Promise<void> {
  for (const event of events) {
    if (event.type === "tick") {
      tickCount.value = event.tick;
      displayCard("bull", event.bullCard);
      displayCard("bear", event.bearCard);
      await wait(600);

      const prevIndex = priceIndex.value;
      priceIndex.value = event.priceIndex;
      pushCandle(prevIndex, event.priceIndex);
      if (Math.abs(event.movement) >= 2) shakeStage();
      if (event.label === "moonHit") {
        moonHit.value = true;
        sound.moon();
        flashStage(FLASH.moon);
      }
      if (event.label === "crashHit") {
        crashHit.value = true;
        sound.crash();
        shakeStage();
        flashStage(FLASH.crash);
      }
      if (event.label === "volatilitySpike") sound.spike();

      showStatus(tickLabelText(event), TICK_LABEL_COLOR[event.label]);
      await wait(1500);
    } else if (event.type === "decision") {
      // The outcome is predetermined by the book — the prompt is
      // presentational; any click acknowledges it, then the book's action
      // is revealed.
      computeDecisionOffer();
      sound.offer();
      showDecision.value = true;
      await new Promise<void>((resolve) => {
        decisionResolver = resolve;
      });
      showDecision.value = false;

      if (event.action === "takeProfit") {
        sound.cashout();
        showStatus("PROFIT LOCKED", "cc-c-gold", 1500);
        await wait(1200);
      } else if (event.action === "takeLoss") {
        sound.click();
        showStatus("LOSS CUT", "cc-c-crash", 1500);
        await wait(1200);
      } else {
        showStatus("HOLDING...", "cc-c-info", 1000);
        await wait(900);
      }
    }
    // roundStart: board is already reset. setTotalWin/finalWin: the final
    // status is shown from round.payout in startServerRound.
  }
}

async function startServerRound() {
  const betType = currentBetType.value as BetType;
  isPlaying.value = true;
  resetBoard();
  sound.marketOpen();
  showStatus("MARKET OPEN", "cc-c-info");

  let round: Round;
  try {
    round = await rgs.play(
      Math.round(wager.value * API_MULTIPLIER),
      BET_MODE[betType]
    );
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : String(e);
    sound.error();
    showStatus(words.betRejected, "cc-c-down", 2000);
    isPlaying.value = false;
    return;
  }

  await wait(800);
  const events = Array.isArray(round.state) ? (round.state as BookEvent[]) : [];
  if (events.some((event) => event.type === "tick")) {
    await replayBookEvents(events);
  } else {
    // No book events in the response — synthetic fallback.
    await animateServerOutcome(betType, round);
  }

  try {
    if (rgs.roundActive.value) await rgs.endRound();
  } catch (e) {
    console.error("EndRound failed", e);
  }

  const payout = round.payout ?? 0;
  if (payout > 0) {
    sound.win();
    burstConfetti();
    showStatus(`WIN +${formatPayout(payout)}`, "cc-c-gold", 2000);
  } else {
    sound.lose();
    showStatus("LIQUIDATED", "cc-c-muted", 2000);
  }

  addToHistory(5 - priceIndex.value);
  finishRound();
}

function formatPayout(apiAmount: number): string {
  const currency = rgs.balance.value?.currency;
  if (currency) {
    return DisplayAmount(
      { amount: apiAmount, currency },
      { trimDecimalForIntegers: true }
    );
  }
  return `$${ParseAmount(apiAmount)}`;
}

async function animateServerOutcome(betType: BetType, round: Round) {
  const win = (round.payoutMultiplier ?? 0) > 0;
  const target = pickTargetIndex(betType, win);
  let current = 5;

  for (let tick = 1; tick <= maxTick; tick++) {
    tickCount.value = tick;
    const step = pickStep(current, target, maxTick - tick);
    const [bullVal, bearVal] = cardsForStep(step);
    displayCard("bull", bullVal);
    displayCard("bear", bearVal);
    await wait(600);

    const prevIndex = current;
    current += step;
    priceIndex.value = current;
    pushCandle(prevIndex, current);

    let statusMsg: string;
    let color: string;
    if (step <= -2) {
      statusMsg = `${words.strongBuy} (+${-step})`;
      color = "cc-c-up";
    } else if (step === -1) {
      statusMsg = "BULLISH";
      color = "cc-c-up";
    } else if (step >= 2) {
      statusMsg = `PANIC SELL (-${step})`;
      color = "cc-c-down";
    } else if (step === 1) {
      statusMsg = "BEARISH";
      color = "cc-c-down";
    } else {
      statusMsg = "SIDEWAYS CHOP";
      color = "cc-c-moon";
    }

    if (current === 0) {
      moonHit.value = true;
      statusMsg = "MOON HIT +5";
      sound.moon();
      flashStage(FLASH.moon);
    }
    if (current === 10) {
      crashHit.value = true;
      statusMsg = "CRASH HIT −5";
      sound.crash();
      shakeStage();
      flashStage(FLASH.crash);
    }

    showStatus(statusMsg, color);
    await wait(1500);
  }
}

// Final LED position consistent with the server's win/lose verdict.
// Remember: index 0 is the top of the strip (MOON), 10 the bottom (CRASH).
function pickTargetIndex(betType: BetType, win: boolean): number {
  switch (betType) {
    case "bull":
      return win ? randInt(1, 4) : randInt(5, 9);
    case "bear":
      return win ? randInt(6, 9) : randInt(1, 5);
    case "doji": {
      if (win) return 5;
      const offCenter = [1, 2, 3, 4, 6, 7, 8, 9];
      return offCenter[Math.floor(Math.random() * offCenter.length)];
    }
    case "moon":
      return win ? 0 : randInt(2, 9);
    case "crash":
      return win ? 10 : randInt(1, 8);
  }
}

// Random tick movement that stays on the board, keeps the extremes for the
// final tick, and can still reach the target within the remaining ticks.
function pickStep(
  current: number,
  target: number,
  ticksLeftAfter: number
): number {
  const candidates: number[] = [];
  for (let step = -2; step <= 2; step++) {
    const next = current + step;
    if (next < 0 || next > 10) continue;
    if (ticksLeftAfter > 0 && (next === 0 || next === 10)) continue;
    if (Math.abs(target - next) > 2 * ticksLeftAfter) continue;
    candidates.push(step);
  }
  return candidates[Math.floor(Math.random() * candidates.length)] ?? 0;
}

// Card faces that visually justify the movement of this tick.
function cardsForStep(step: number): [number, number] {
  if (step === 0) {
    const tie = randInt(2, 14);
    return [tie, tie];
  }
  const big = Math.abs(step) >= 2;
  const loser = big ? randInt(2, 10) : randInt(2, 9);
  const winner = big ? randInt(11, 14) : randInt(loser + 1, 10);
  return step < 0 ? [winner, loser] : [loser, winner];
}

function randInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

async function gameLoop() {
  // Reached the 5-tick cap: settle whatever position stands.
  if (tickCount.value >= maxTick) {
    await settleRound();
    return;
  }

  tickCount.value++;

  // 1. Draw + animate cards
  const bullVal = getRandomCard();
  const bearVal = getRandomCard();
  displayCard("bull", bullVal);
  displayCard("bear", bearVal);
  await wait(600);

  // 2. Resolve the tick movement
  const prevIndex = priceIndex.value;
  let movement = 0;
  let statusMsg = "";
  let color = "cc-c-default";
  let baseStep = 1;

  if (bullVal > bearVal) {
    if (bullVal >= 11) baseStep = 2;
    const totalSteps = baseStep * volatility.value;
    movement = -totalSteps;
    statusMsg =
      totalSteps >= 2 ? `${words.strongBuy} (+${totalSteps})` : "BULLISH";
    color = "cc-c-up";
    volatility.value = 1;
  } else if (bearVal > bullVal) {
    if (bearVal >= 11) baseStep = 2;
    const totalSteps = baseStep * volatility.value;
    movement = totalSteps;
    statusMsg = totalSteps >= 2 ? `PANIC SELL (-${totalSteps})` : "BEARISH";
    color = "cc-c-down";
    volatility.value = 1;
  } else {
    // Tie -> volatility spike (next move doubled)
    movement = 0;
    volatility.value = 2;
    statusMsg = "VOLATILITY SPIKE";
    color = "cc-c-moon";
    sound.spike();
  }

  priceIndex.value += movement;
  if (Math.abs(movement) >= 2) shakeStage();

  // 3. Clamp + extreme hits (instant end)
  let ended = false;
  if (priceIndex.value <= 0) {
    priceIndex.value = 0;
    moonHit.value = true;
    statusMsg = "MOON HIT +5";
    color = "cc-c-moon";
    sound.moon();
    flashStage(FLASH.moon);
    ended = true;
  } else if (priceIndex.value >= 10) {
    priceIndex.value = 10;
    crashHit.value = true;
    statusMsg = "CRASH HIT −5";
    color = "cc-c-crash";
    sound.crash();
    shakeStage();
    flashStage(FLASH.crash);
    ended = true;
  }

  pushCandle(prevIndex, priceIndex.value);
  showStatus(statusMsg, color);
  await wait(1500);

  if (ended) {
    await settleRound();
    return;
  }

  // 4. Decision point: BULL/BEAR positions choose every tick until the cap.
  const directional =
    currentBetType.value === "bull" || currentBetType.value === "bear";
  if (directional && tickCount.value < maxTick) {
    promptDecision();
    return;
  }

  gameLoop();
}

// --- PER-TICK DECISION (Take Profit / Take Loss / Hold) ---
// In live mode the modal resolves a pending promise inside replayBookEvents and
// the predetermined book action plays out. In local mode the choice is real.
let decisionResolver: (() => void) | null = null;

function resolveLiveDecision(): boolean {
  if (!decisionResolver) return false;
  const resolve = decisionResolver;
  decisionResolver = null;
  showDecision.value = false;
  resolve();
  return true;
}

function promptDecision() {
  computeDecisionOffer();
  sound.offer();
  showDecision.value = true;
}

function chooseProfit() {
  if (profitAmount.value <= 0) return;
  sound.cashout();
  if (resolveLiveDecision()) return;
  showDecision.value = false;
  const win = profitAmount.value;
  demoBalance.value += win;
  burstConfetti(["#ffd23f", "#00e5ff", "#ff2e97"], 16);
  showStatus(`PROFIT LOCKED +$${win}`, "cc-c-gold", 2500);
  addToHistory(5 - priceIndex.value);
  finishRound();
}

function chooseLoss() {
  if (lossRefund.value <= 0) return;
  sound.click();
  if (resolveLiveDecision()) return;
  showDecision.value = false;
  const refund = lossRefund.value;
  demoBalance.value += refund;
  showStatus(`LOSS CUT +$${refund}`, "cc-c-crash", 2500);
  addToHistory(5 - priceIndex.value);
  finishRound();
}

async function chooseHold() {
  sound.click();
  if (resolveLiveDecision()) return;
  showDecision.value = false;
  showStatus("HOLDING...", "cc-c-info", 900);
  await wait(900);
  gameLoop();
}

// --- SETTLEMENT (cap reached or extreme hit) ---
async function settleRound() {
  await wait(300);
  const betType = currentBetType.value;
  const finalValue = 5 - priceIndex.value;
  let winAmount = 0;

  if (betType === "moon" && moonHit.value)
    winAmount = wager.value * PAYOUTS.moon;
  else if (betType === "crash" && crashHit.value)
    winAmount = wager.value * PAYOUTS.crash;
  else if (betType === "bull" && finalValue > 0)
    winAmount = Math.floor(
      wager.value * DISTANCE_LADDER[Math.min(finalValue, 5)]
    );
  else if (betType === "bear" && finalValue < 0)
    winAmount = Math.floor(
      wager.value * DISTANCE_LADDER[Math.min(-finalValue, 5)]
    );
  else if (betType === "doji" && finalValue === 0)
    winAmount = wager.value * PAYOUTS.dojiExact;
  else if (betType === "doji" && Math.abs(finalValue) === 1)
    winAmount = wager.value * PAYOUTS.dojiNear;

  if (winAmount > 0) {
    demoBalance.value += winAmount;
    sound.win();
    burstConfetti();
    showStatus(`WIN +$${winAmount}`, "cc-c-gold", 2000);
  } else {
    sound.lose();
    showStatus("LIQUIDATED", "cc-c-muted", 2000);
  }

  addToHistory(finalValue);
  finishRound();
}

function finishRound() {
  isPlaying.value = false;
  hasPlayed.value = true;
  currentBetType.value = null;
}

// --- HELPERS ---
function getRandomCard(): number {
  return Math.floor(Math.random() * 13) + 2;
}

function displayCard(side: "bull" | "bear", val: number) {
  let displayVal = String(val);
  if (val === 11) displayVal = "J";
  if (val === 12) displayVal = "Q";
  if (val === 13) displayVal = "K";
  if (val === 14) displayVal = "A";

  if (side === "bull") {
    // One flip sound per tick (bull + bear always flip together).
    sound.cardFlip();
    bullCard.value = displayVal;
    bullBounce.value = true;
    setTimeout(() => (bullBounce.value = false), 500);
  } else {
    bearCard.value = displayVal;
    bearBounce.value = true;
    setTimeout(() => (bearBounce.value = false), 500);
  }
}

function showStatus(text: string, color: string, duration = 1000) {
  statusText.value = text;
  statusColor.value = color;
  statusVisible.value = true;
  if (statusTimer) clearTimeout(statusTimer);
  statusTimer = setTimeout(() => {
    statusVisible.value = false;
  }, duration);
}

function addToHistory(val: number) {
  history.value.unshift(val);
  if (history.value.length > 20) history.value.pop();
}

function historyPillClass(val: number): string {
  const base = "cc-hist cc-mono";
  if (val > 0) return `${base} is-up`;
  if (val < 0) return `${base} is-down`;
  return `${base} is-flat`;
}

function historyPillText(val: number): string {
  if (val > 0) return `+${val}`;
  if (val < 0) return String(val);
  return "0";
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
</script>

<style scoped>
.cc-root {
  --bg0: #0b0518;
  --bg1: #140a28;
  --panel: #191030;
  --panel-2: #241447;
  --panel-3: #180b2e;
  --line: rgba(180, 140, 255, 0.18);
  --ink: #f5f0ff;
  --muted: #a596c4;
  --dim: #6b5b8a;
  --up: #00e676;
  --down: #ff1744;
  --gold: #ffd23f;
  --moon: #b14bff;
  --crash: #ff6d00;
  --pink: #ff2e97;
  --cyan: #00e5ff;
  --radius: 14px;
  --font-display: "Orbitron", "Inter", system-ui, sans-serif;

  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 18px;
  color: var(--ink);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Trebuchet MS",
    Roboto, sans-serif;
  background: var(--bg0);
}

.cc-mono {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-variant-numeric: tabular-nums;
}
.cc-accent {
  color: var(--pink);
}

.cc-c-up {
  color: var(--up);
}
.cc-c-down {
  color: var(--down);
}
.cc-c-gold {
  color: var(--gold);
}
.cc-c-moon {
  color: var(--moon);
}
.cc-c-crash {
  color: var(--crash);
}
.cc-c-info {
  color: var(--cyan);
}
.cc-c-muted {
  color: var(--muted);
}
.cc-c-default {
  color: var(--ink);
}

/* ---------- LOADING ---------- */
.cc-loading {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  background: var(--bg0);
}
.cc-loader {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 80px;
}
.cc-loading-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 900;
  letter-spacing: 1px;
}
.cc-loading-bar {
  width: 240px;
  height: 6px;
  border-radius: 999px;
  background: var(--panel-2);
  border: 1px solid var(--line);
  overflow: hidden;
}
.cc-loading-fill {
  height: 100%;
  border-radius: 999px;
  background: var(--pink);
  transition: width 0.2s ease;
}
.cc-loading-label {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--muted);
}
.loading-fade-leave-active {
  transition: opacity 0.5s ease-out;
}
.loading-fade-leave-to {
  opacity: 0;
}
.loader-candle {
  width: 12px;
  height: 46px;
  border-radius: 3px;
  position: relative;
  animation: loader-bounce 1s ease-in-out infinite;
}
.loader-candle::before {
  content: "";
  position: absolute;
  left: 50%;
  top: -10px;
  bottom: -10px;
  width: 2px;
  transform: translateX(-50%);
  background: inherit;
  opacity: 0.7;
  z-index: -1;
}
.loader-candle:nth-child(odd) {
  background: var(--up);
}
.loader-candle:nth-child(even) {
  background: var(--down);
}
@keyframes loader-bounce {
  0%,
  100% {
    transform: translateY(0) scaleY(0.6);
  }
  50% {
    transform: translateY(-14px) scaleY(1);
  }
}

/* ---------- HEADER ---------- */
.cc-header {
  width: 100%;
  max-width: 1120px;
  height: 62px;
  margin-bottom: 16px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: var(--radius);
  border: 1px solid var(--line);
  background: var(--panel);
}
.cc-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cc-brand-badge {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: var(--panel-2);
  border: 1px solid var(--line);
}
.cc-badge-svg {
  width: 22px;
  height: 22px;
}
.cc-brand-meta {
  display: flex;
  align-items: center;
  gap: 9px;
}
.cc-brand-name {
  margin: 0;
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 800;
  letter-spacing: 0.4px;
}
.cc-brand-dot {
  color: var(--dim);
}
.cc-brand-sub {
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted);
}
.cc-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.cc-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.cc-status-chip.is-live {
  background: rgba(0, 230, 118, 0.16);
  color: var(--up);
}
.cc-status-chip.is-live .cc-status-dot {
  background: var(--up);
}
.cc-status-chip.is-local {
  background: var(--panel-2);
  color: var(--muted);
}
.cc-status-chip.is-local .cc-status-dot {
  background: var(--muted);
}
.cc-header-right {
  display: flex;
  align-items: center;
  gap: 14px;
}
.cc-balance {
  text-align: right;
  line-height: 1.15;
}
.cc-balance-label {
  margin: 0;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}
.cc-balance-value {
  margin: 0;
  font-size: 21px;
  font-weight: 600;
  color: var(--gold);
}
.cc-icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  color: var(--muted);
  background: var(--panel-2);
  border: 1px solid var(--line);
  cursor: pointer;
  transition: all 0.15s ease;
}
.cc-icon-btn:hover {
  color: var(--ink);
  border-color: var(--pink);
}
.cc-icon {
  width: 18px;
  height: 18px;
}

/* ---------- MOBILE HEADER ---------- */
@media (max-width: 600px) {
  .cc-root {
    padding: 10px;
  }
  .cc-header {
    height: auto;
    min-height: 52px;
    margin-bottom: 12px;
    padding: 8px 12px;
    gap: 8px;
  }
  .cc-brand {
    gap: 8px;
    min-width: 0;
    flex: 1;
  }
  .cc-brand-meta {
    gap: 7px;
    min-width: 0;
  }
  .cc-brand-badge {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
  }
  .cc-badge-svg {
    width: 18px;
    height: 18px;
  }
  .cc-brand-name {
    font-size: 15px;
    white-space: nowrap;
  }
  /* Secondary labels don't fit on a phone row */
  .cc-brand-dot,
  .cc-brand-sub {
    display: none;
  }
  .cc-header-right {
    gap: 8px;
    flex-shrink: 0;
  }
  .cc-balance-label {
    font-size: 9px;
  }
  .cc-balance-value {
    font-size: 16px;
  }
  .cc-icon-btn {
    width: 34px;
    height: 34px;
  }
}

/* ---------- MAIN LAYOUT ---------- */
.cc-main {
  width: 100%;
  max-width: 1120px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}
@media (min-width: 900px) {
  .cc-main {
    grid-template-columns: 1.9fr 1fr;
  }
}
.cc-board {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ---------- STAGE ---------- */
.cc-stage {
  position: relative;
  height: 460px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  border-radius: var(--radius);
  border: 1px solid var(--line);
  background: #0d0620;
}
.cc-flash {
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
}
.cc-confetti-layer {
  position: absolute;
  inset: 0;
  z-index: 9;
  pointer-events: none;
  overflow: hidden;
}
.cc-side {
  position: relative;
  z-index: 2;
  width: 25%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.cc-side-label {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
}
.cc-side-label.is-bull {
  color: var(--up);
}
.cc-side-label.is-bear {
  color: var(--down);
}
.cc-card {
  width: 94px;
  height: 142px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--panel-2);
}
.card-slot {
  perspective: 500px;
}
.cc-card-face {
  font-size: 44px;
  font-weight: 700;
  line-height: 1;
}
.cc-card-face.is-empty {
  color: var(--dim);
}
.cc-card-face.is-bull {
  color: var(--up);
}
.cc-card-face.is-bear {
  color: var(--down);
}
.card-flip {
  animation: card-flip 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  backface-visibility: hidden;
}
@keyframes card-flip {
  0% {
    transform: rotateY(90deg) scale(0.8);
    opacity: 0;
  }
  100% {
    transform: rotateY(0deg) scale(1);
    opacity: 1;
  }
}
.cc-chart {
  position: relative;
  z-index: 3;
  flex: 1;
  height: 100%;
  min-width: 0;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cc-chart-svg {
  height: 100%;
  width: 100%;
  max-width: 380px;
}
.chart-label {
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 1.5px;
}
.chart-empty {
  font-size: 9px;
  letter-spacing: 2.5px;
}
.candle rect,
.candle line {
  animation: candle-in 0.35s ease-out backwards;
  transform-box: fill-box;
  transform-origin: center;
}
@keyframes candle-in {
  from {
    opacity: 0;
    transform: scaleY(0.2);
  }
  to {
    opacity: 1;
    transform: scaleY(1);
  }
}
.price-dot {
  animation: dot-pulse 1.2s ease-in-out infinite;
}
@keyframes dot-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

/* ---------- STATUS OVERLAY ---------- */
.cc-status-overlay {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: grid;
  place-items: center;
  pointer-events: none;
}
.cc-status-text {
  font-family: var(--font-display);
  font-size: 30px;
  font-weight: 900;
  text-align: center;
  letter-spacing: 0.5px;
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.cc-status-text.is-visible {
  transform: scale(1);
  opacity: 1;
}
.cc-status-text.is-hidden {
  transform: scale(0.5);
  opacity: 0;
}

/* ---------- DECISION / CASH OUT ---------- */
.cc-cashout {
  position: absolute;
  inset: 0;
  z-index: 8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 0 12px;
  background: rgba(8, 4, 20, 0.9);
}
.cc-cashout-title {
  font-family: var(--font-display);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--gold);
}
.cc-cashout-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}
.cc-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 108px;
  padding: 12px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
  border: none;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.1s ease;
}
.cc-btn .cc-mono {
  font-size: 14px;
  font-weight: 700;
}
.cc-btn:active {
  transform: translateY(1px);
}
.cc-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.cc-btn:disabled:active {
  transform: none;
}
.cc-btn-cashout,
.cc-btn-profit {
  color: #04120b;
  background: var(--up);
}
.cc-btn-cashout:hover,
.cc-btn-profit:not(:disabled):hover {
  filter: brightness(1.08);
}
.cc-btn-loss {
  color: #1a0a05;
  background: var(--crash);
}
.cc-btn-loss:not(:disabled):hover {
  filter: brightness(1.08);
}
.cc-btn-hold {
  color: var(--ink);
  background: var(--panel-2);
  border: 1px solid var(--line);
}
.cc-btn-hold:hover {
  filter: brightness(1.15);
}
.cc-cashout-note {
  margin: 2px 0 0;
  max-width: 340px;
  text-align: center;
  font-size: 11px;
  color: var(--muted);
}

/* ---------- HISTORY ---------- */
.cc-history {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 56px;
  padding: 0 14px;
  border-radius: var(--radius);
  border: 1px solid var(--line);
  background: var(--panel);
  overflow-x: auto;
}
.cc-history-label {
  flex-shrink: 0;
  padding-right: 4px;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}
.cc-hist {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}
.cc-hist.is-up {
  background: var(--up);
  color: #04120b;
}
.cc-hist.is-down {
  background: var(--down);
  color: #fff;
}
.cc-hist.is-flat {
  background: #e8def7;
  color: var(--bg1);
}

/* ---------- ORDER PANEL ---------- */
.cc-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border-radius: var(--radius);
  border: 1px solid var(--line);
  background: var(--panel);
}
.cc-field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
}
.cc-chips {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.cc-chip {
  padding: 9px 0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  background: var(--panel-2);
  border: 1px solid var(--line);
  cursor: pointer;
  transition: all 0.15s ease;
}
.cc-chip:hover {
  color: var(--ink);
  border-color: var(--pink);
}
.cc-chip.is-selected {
  color: #fff;
  border-color: var(--pink);
  background: var(--pink);
}
.cc-input {
  width: 100%;
  margin-top: 8px;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 14px;
  color: var(--ink);
  background: var(--panel-2);
  border: 1px solid var(--line);
  outline: none;
  transition: border-color 0.15s ease;
}
.cc-input::placeholder {
  color: var(--dim);
}
.cc-input:focus,
.cc-input.is-active {
  border-color: var(--pink);
}
.cc-divider {
  height: 1px;
  width: 100%;
  background: var(--line);
}

/* ---------- BET BUTTONS ---------- */
.cc-bets-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.cc-bet {
  position: relative;
  height: 86px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border-radius: 12px;
  border: 1px solid var(--line);
  cursor: pointer;
  overflow: hidden;
  transition: all 0.15s ease;
}
.cc-bet.is-bull {
  background: rgba(0, 230, 118, 0.12);
  border-color: rgba(0, 230, 118, 0.5);
}
.cc-bet.is-bull:hover {
  border-color: var(--up);
  transform: translateY(-2px);
}
.cc-bet.is-bear {
  background: rgba(255, 23, 68, 0.12);
  border-color: rgba(255, 23, 68, 0.5);
}
.cc-bet.is-bear:hover {
  border-color: var(--down);
  transform: translateY(-2px);
}
.cc-bet-mult {
  position: absolute;
  top: 8px;
  right: 10px;
  font-size: 10px;
  color: var(--muted);
}
.cc-bet-arrow {
  font-size: 20px;
  line-height: 1;
}
.cc-bet.is-bull .cc-bet-arrow,
.cc-bet.is-bull .cc-bet-name {
  color: var(--up);
}
.cc-bet.is-bear .cc-bet-arrow,
.cc-bet.is-bear .cc-bet-name {
  color: var(--down);
}
.cc-bet-name {
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.06em;
}
.cc-bet-desc {
  font-size: 10px;
  color: var(--muted);
}
.cc-bet-row {
  position: relative;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--panel-2);
  cursor: pointer;
  transition: all 0.15s ease;
}
.cc-bet-row:hover {
  border-color: var(--gold);
}
.cc-bet-row-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cc-bet-row-icon {
  color: var(--ink);
  font-weight: 700;
}
.cc-bet-row-meta {
  display: flex;
  flex-direction: column;
  text-align: left;
}
.cc-bet-row-name {
  font-weight: 700;
  color: var(--ink);
}
.cc-bet-row-desc {
  font-size: 10px;
  color: var(--muted);
}
.cc-bet-row-pay {
  color: var(--gold);
  font-weight: 700;
}
.cc-bet-mini {
  position: relative;
  height: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--panel-2);
  cursor: pointer;
  transition: all 0.15s ease;
}
.cc-bet-mini.is-moon:hover {
  border-color: var(--moon);
}
.cc-bet-mini.is-crash:hover {
  border-color: var(--crash);
}
.cc-bet-mini-sub {
  font-size: 10px;
  color: var(--muted);
}
.cc-bet-mini-name {
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.04em;
}
.cc-bet-mini.is-moon .cc-bet-mini-name {
  color: var(--moon);
}
.cc-bet-mini.is-crash .cc-bet-mini-name {
  color: var(--crash);
}
.cc-bet-selected {
  border-color: var(--cyan) !important;
  box-shadow: 0 0 0 1px var(--cyan) !important;
}
.cc-bet-dimmed {
  opacity: 0.42;
}

/* ---------- ACTION ---------- */
.cc-action {
  width: 100%;
  margin-top: 4px;
  padding: 15px;
  border-radius: 12px;
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.08em;
  border: none;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.1s ease;
}
.cc-action.is-ready {
  color: #0a0414;
  background: var(--pink);
}
.cc-action.is-ready:hover {
  filter: brightness(1.08);
}
.cc-action.is-ready:active {
  transform: translateY(1px);
}
.cc-action.is-disabled {
  color: var(--dim);
  background: var(--panel-2);
  cursor: not-allowed;
}
.cc-betinfo {
  height: 16px;
  text-align: center;
  font-size: 11px;
  color: var(--muted);
}
.cc-betinfo.is-error {
  color: var(--down);
}

/* ---------- FOOTER ---------- */
.cc-footer {
  width: 100%;
  max-width: 1120px;
  margin-top: 18px;
  text-align: center;
  font-size: 11px;
  color: var(--muted);
}
.cc-footer-sub {
  margin-top: 4px;
}
.cc-legal {
  margin: 14px auto 0;
  max-width: 640px;
  padding: 0 16px;
  font-size: 10px;
  line-height: 1.5;
  color: var(--dim);
}

/* ---------- CHART TYPE TOGGLE ---------- */
.cc-chart-toggle {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 7;
  display: flex;
  gap: 2px;
  padding: 3px;
  border-radius: 9px;
  border: 1px solid var(--line);
  background: rgba(13, 6, 32, 0.72);
}
.cc-chart-toggle-btn {
  display: grid;
  place-items: center;
  width: 28px;
  height: 24px;
  border: none;
  border-radius: 6px;
  color: var(--muted);
  background: transparent;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}
.cc-chart-toggle-btn svg {
  width: 16px;
  height: 16px;
}
.cc-chart-toggle-btn:hover {
  color: var(--ink);
}
.cc-chart-toggle-btn.is-active {
  color: #0a0414;
  background: var(--cyan);
}
.cc-line {
  filter: drop-shadow(0 0 4px rgba(0, 229, 255, 0.35));
}

/* ---------- STAGE FX ---------- */
.stage-shake {
  animation: stage-shake 0.4s ease-out;
}
@keyframes stage-shake {
  0%,
  100% {
    transform: translate(0, 0);
  }
  20% {
    transform: translate(-6px, 3px);
  }
  40% {
    transform: translate(5px, -4px);
  }
  60% {
    transform: translate(-4px, 2px);
  }
  80% {
    transform: translate(3px, -2px);
  }
}
.stage-flash {
  animation: stage-flash 0.6s ease-out forwards;
}
@keyframes stage-flash {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
.stage-final {
  animation: final-pulse 1.1s ease-in-out infinite;
}
@keyframes final-pulse {
  0%,
  100% {
    border-color: var(--line);
  }
  50% {
    border-color: var(--cyan);
  }
}
.confetti {
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  animation: confetti-burst 0.9s ease-out forwards;
  opacity: 0;
}
@keyframes confetti-burst {
  0% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translate(var(--dx), var(--dy)) rotate(540deg);
    opacity: 0;
  }
}
</style>
